import React, { useCallback, useEffect, useRef, useState } from "react";

import Icon from "./Icon";
import "./ScrollToTop.scss";

/**
 * ScrollToTop — a back-to-top control that appears once the page has been
 * scrolled, pinned bottom-right.
 *
 * Long grouped pages (the Core List runs to 116 references) leave you a long
 * way from the filter bar with no way back but a scroll.
 *
 * WHICH ELEMENT SCROLLS IS NOT FIXED IN THIS APP. Some routes scroll the
 * window; others sit inside a panel that scrolls instead, and on phones the
 * scroller can differ again. Rather than make every caller know which, this
 * listens in the CAPTURE phase on window: `scroll` does not bubble, but it
 * does capture, so one listener sees scrolling from ANY element and the event
 * itself names the scroller. Falls back to the document when nothing has
 * scrolled yet.
 *
 * @param {number} [showAfter=240]    px of scroll before it appears
 * @param {string} [label]            accessible name
 */
const ScrollToTop = ({ showAfter = 240, label = "Back to top", className = "" }) => {
  const [visible, setVisible] = useState(false);
  // The element that last scrolled — what "top" should mean when clicked.
  const scroller = useRef(null);

  useEffect(() => {
    const offsetOf = (node) => {
      if (!node || node === document || node === document.documentElement || node === window) {
        return window.scrollY || document.documentElement.scrollTop || 0;
      }
      return node.scrollTop || 0;
    };

    const onScroll = (event) => {
      const node = event.target;
      scroller.current = node;
      setVisible(offsetOf(node) > showAfter);
    };

    // Capture phase — see the note above. Passive: this never calls
    // preventDefault, and saying so keeps scrolling off the main thread.
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });

    // The page may already be scrolled when this mounts (a back-navigation
    // restores position), and waiting for an event would hide the control
    // exactly when it is most useful.
    setVisible(offsetOf(document.documentElement) > showAfter);

    return () =>
      window.removeEventListener("scroll", onScroll, { capture: true, passive: true });
  }, [showAfter]);

  const toTop = useCallback(() => {
    // `smooth` is ignored under prefers-reduced-motion by every current
    // engine, so no separate branch is needed.
    const options = { top: 0, behavior: "smooth" };
    const node = scroller.current;

    if (node && node !== document && node !== document.documentElement && node.scrollTo) {
      node.scrollTo(options);
      return;
    }
    window.scrollTo(options);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      className={["ft-to-top", className].filter(Boolean).join(" ")}
      onClick={toTop}
      aria-label={label}
      title={label}
    >
      {/* A caret, not an arrow: no shaft, so it reads at 40px and sits*/}
      {/* quietly over the list it floats on. */}
      <Icon name="ChevronUp" size="md" />
    </button>
  );
};

export default ScrollToTop;
